import { Button, IconButton, Stack, Typography, Box } from "@mui/material";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import colorConfigs from "../../../configs/colorConfigs";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { ICartItem } from "../../../models/ICart";
import useSWR from "swr";
import { PATH_NAME } from "../../../configs/pathName";
import { generalFetcher } from "../../../helpers/Fetchers/fetchers";
import { ISkuDetail } from "../../../models/ISkuDetail";
import { typo } from "../../../assets/fonts/font";
import { AWS_URL } from "../../../configs/constants";

interface CartDrawerItemProps {
  item: ICartItem;
  removeItem: (item: ICartItem) => void;
  reduceItemQuantity: (item: ICartItem) => void;
  increaseItemQuantity: (item: ICartItem) => void;
  readOnly?: boolean;
}

const CartDrawerItem = ({
  item,
  removeItem,
  reduceItemQuantity,
  increaseItemQuantity,
  readOnly = false,
}: CartDrawerItemProps) => {
  const itemTotal = item.price * item.quantity;
  const { data: skuDetail }: { data: ISkuDetail } = useSWR(
    PATH_NAME.API_GET_SKU_DETAILS_BY_SKU(item.sku_sku),
    generalFetcher
  );

  const handleRemoveItem = () => {
    removeItem(item);
  };

  const handleReduceItemQuantity = () => {
    reduceItemQuantity(item);
  };

  const handleIncreaseItemQuantity = () => {
    increaseItemQuantity(item);
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: "center",
        width: "100%",
        border: "1px solid var(--color-black)",
        borderRadius: "8px",
      }}
    >
      <Box
        component="img"
        src={`${AWS_URL}/${skuDetail?.image_url ?? ''}`}
        alt="product"
        sx={{
          width: {
            xs: "75px",
            sm: "100px",
          },
          height: "100%",
          objectFit: "cover",
          borderRadius: "8px",
          padding: "0.5rem",
        }}
      />
      <Stack
        direction="column"
        sx={{
          height: "100%",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        {/* Conditionally render remove button */}
        {!readOnly && (
          <IconButton
            sx={{
              color: colorConfigs.secondary,
              fontSize: "0.5rem",
              borderRadius: "5rem",
              alignSelf: "flex-end",
              width: "2rem",
              height: "2rem",
            }}
            onClick={handleRemoveItem}
          >
            <ClearRoundedIcon sx={{ color: colorConfigs.tertiary }} />
          </IconButton>
        )}
        
        <Typography
          sx={{
            maxWidth: "100%",
            overflow: "hidden",
            ...typo.bodyContent,
          }}
        >
          {skuDetail ? skuDetail.name : ""}
        </Typography>
        
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: "space-between", alignItems: "center", py: 1 }}
        >
          {/* Quantity controls - conditionally rendered */}
          {!readOnly ? (
            <Stack
              direction="row"
              sx={{
                alignItems: "center",
                borderRadius: "5rem",
                padding: 0,
                width: "max-content",
                height: "max-content",
              }}
            >
              <Button
                variant="text"
                sx={{
                  borderRadius: "5rem",
                  border: "2px solid var(--color-black)",
                  color: colorConfigs.tertiary,
                  width: "2rem",
                  height: "2rem",
                  minWidth: "2rem",
                  padding: 0,
                }}
                onClick={handleReduceItemQuantity}
              >
                <Typography sx={{ ...typo.bodyContent }}>
                  <RemoveIcon sx={{ fontSize: "1.2rem" }} />
                </Typography>
              </Button>
              <Typography sx={{ ...typo.bodyContent, px: 1 }}>
                {item.quantity}
              </Typography>
              <Button
                variant="text"
                sx={{
                  borderRadius: "5rem",
                  border: "2px solid var(--color-black)",
                  color: colorConfigs.tertiary,
                  width: "2rem",
                  height: "2rem",
                  minWidth: "2rem",
                  padding: 0,
                }}
                onClick={handleIncreaseItemQuantity}
              >
                <AddIcon sx={{ fontSize: "1.2rem" }} />
              </Button>
            </Stack>
          ) : (
            // Read-only quantity display
            <Typography sx={{ ...typo.bodyContent }}>
              Qty: {item.quantity}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ alignItems: "center", pr:2 }}>
            <Typography sx={{ ...typo.title }}>
              ${itemTotal.toFixed(2)}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CartDrawerItem;